<script setup lang="ts">
// An input's audio history (audiohist.ts): one strip per channel coloured
// by its loudest sample each second (or minute) on the live meters' scale,
// blank for silence; a clip marked white, a dropout blue. Under it the
// short-term loudness per stereo pair against -23 LUFS. Hover reads every
// channel at that moment. The strips are a canvas: an hour of 16 channels
// is 57,600 cells.
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import type { Sample } from '../api'
import {
    band,
    bandAlpha,
    channelsShown,
    cellEnd,
    dbAt,
    fmtDB,
    marks,
    pairLabel,
    pairsShown,
    stepAt,
    type Band,
} from '../audiohist'
import { clock, dayLabel, tipPos } from '../util'

const props = defineProps<{
    samples: Sample[]
    from: number // ms: the window the graph covers
    to: number
    label: string
}>()

const lufsH = 48
const lufsLo = -50,
    lufsHi = -5,
    target = -23
const pairColours = ['var(--accent)', '#b083f0', '#3fb9b9', '#e07bb8', '#8fbf3f', '#e0a05a', '#7f9cf5', '#c0c4cc']

// the samples stay put while hovered, so a live refresh does not move the
// moment under the pointer
const frozen = ref<Sample[] | null>(null)
const data = computed(() => (frozen.value ?? props.samples).filter((s) => s.peak))
const channels = computed(() => channelsShown(data.value))
// px per channel, 1 px apart: taller when there are few
const rowH = computed(() => (channels.value <= 4 ? 12 : channels.value <= 8 ? 9 : 7))
const pairs = computed(() => pairsShown(data.value))
const span = computed(() => Math.max(1, props.to - props.from))
const frac = (t: number) => (t * 1000 - props.from) / span.value

const canvas = ref<HTMLCanvasElement | null>(null)
const box = ref<HTMLDivElement | null>(null)
const width = ref(0)
let ro: ResizeObserver | undefined
onMounted(() => {
    ro = new ResizeObserver(([e]) => (width.value = Math.round(e!.contentRect.width)))
    if (box.value) ro.observe(box.value)
})
onUnmounted(() => ro?.disconnect())

function draw() {
    const cv = canvas.value
    if (!cv || !width.value) return
    const dpr = window.devicePixelRatio || 1
    const n = channels.value
    const h = n * (rowH.value + 1) - 1
    cv.width = Math.round(width.value * dpr)
    cv.height = Math.round(h * dpr)
    cv.style.height = `${h}px`
    const g = cv.getContext('2d')
    if (!g) return
    g.setTransform(dpr, 0, 0, dpr, 0, 0)
    const css = getComputedStyle(cv)
    const colour: Record<Band, string> = {
        hot: css.getPropertyValue('--bad').trim(),
        good: css.getPropertyValue('--ok').trim(),
        low: css.getPropertyValue('--warn').trim(),
        silent: '',
    }
    g.fillStyle = css.getPropertyValue('--bg-2').trim()
    g.fillRect(0, 0, width.value, h)
    const W = width.value
    const xs = (i: number) => {
        const x0 = frac(data.value[i]!.t) * W
        return [x0, Math.max(frac(cellEnd(data.value, i)) * W - x0, 1)] as const
    }
    data.value.forEach((s, i) => {
        const [x, w] = xs(i)
        if (x + w < 0 || x > W) return
        for (let c = 0; c < n; c++) {
            const db = dbAt(s.peak, c)
            const b = band(db)
            if (b === 'silent') continue
            g.globalAlpha = bandAlpha(db)
            g.fillStyle = colour[b]
            g.fillRect(x, c * (rowH.value + 1), w + 0.5, rowH.value)
        }
    })
    // marks over the levels, at least 2 px wide so a second shows in an hour
    g.globalAlpha = 1
    const markColour = { clip: css.getPropertyValue('--fg').trim(), dropout: css.getPropertyValue('--accent').trim() }
    for (const m of marks(data.value, n)) {
        const [x, w] = xs(m.i)
        g.fillStyle = markColour[m.kind]
        g.fillRect(x, m.c * (rowH.value + 1), Math.max(w, 2), rowH.value)
    }
}
watch([data, channels, width, () => props.from, () => props.to], draw, { flush: 'post' })
onMounted(draw)

// loudness lines per pair, broken at gaps
const lufsPaths = computed(() =>
    pairs.value.map((p) => {
        let d = ''
        let prevEnd = -Infinity // where the sample before ends: a line is broken at a gap
        data.value.forEach((s, i) => {
            const l = s.lufs?.[p] ?? -100
            if (l <= -100) {
                prevEnd = -Infinity
                return
            }
            const x = (frac(s.t) * 1000).toFixed(1)
            const y = (
                lufsH -
                ((Math.min(Math.max(l, lufsLo), lufsHi) - lufsLo) / (lufsHi - lufsLo)) * (lufsH - 4) -
                2
            ).toFixed(1)
            d += `${s.t > prevEnd ? 'M' : 'L'}${x},${y} `
            prevEnd = cellEnd(data.value, i)
        })
        return { p, d, colour: pairColours[p % pairColours.length]! }
    }),
)
const targetY = lufsH - ((target - lufsLo) / (lufsHi - lufsLo)) * (lufsH - 4) - 2

// hover: the nearest sample, every channel and pair at that moment
const hover = ref<{ i: number; x: number; y: number; at: number } | null>(null)
function enter() {
    frozen.value = [...props.samples]
}
function leave() {
    hover.value = null
    frozen.value = null
}
function move(e: MouseEvent) {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const tMs = props.from + ((e.clientX - r.left) / r.width) * span.value
    let best = -1,
        bd = Infinity
    data.value.forEach((s, i) => {
        const d = Math.abs(s.t * 1000 - tMs)
        if (d < bd) [bd, best] = [d, i]
    })
    if (best < 0) return (hover.value = null)
    hover.value = { i: best, at: frac(data.value[best]!.t) * 100, ...tipPos(e, 300, 320) }
}
const tip = computed(() => {
    const s = hover.value ? data.value[hover.value.i] : undefined
    if (!s) return null
    const ms = marks([data.value[hover.value!.i - 1] ?? { t: 0 }, s], channels.value).filter((m) => m.i === 1)
    return {
        when:
            stepAt(data.value, hover.value!.i) >= 60 ? `the minute from ${clock(s.t * 1000)}` : clock(s.t * 1000, true),
        rows: Array.from({ length: channels.value }, (_, c) => ({
            c: c + 1,
            peak: dbAt(s.peak, c),
            floor: dbAt(s.floor, c),
            clips: s.clips?.[c] ?? 0,
            dropout: ms.some((m) => m.c === c && m.kind === 'dropout'),
        })),
        lufs: pairs.value.map((p) => ({ p, l: s.lufs?.[p] ?? -100 })),
    }
})
const ticks = computed(() =>
    [0, 1, 2, 3, 4].map((k) => {
        const d = new Date(props.from + (k / 4) * span.value)
        return { x: k * 25, label: span.value > 2 * 86400e3 ? dayLabel(d) : clock(d, span.value < 600e3) }
    }),
)
</script>

<template>
    <div class="mt-2.5" data-test="audio-history">
        <div class="truncate text-xs text-fg-2">{{ label }}</div>
        <div class="flex flex-wrap items-center gap-x-2.5 text-[10px] text-fg-3">
            <span><i class="inline-block h-2 w-2 rounded-xs bg-bad align-middle" /> above -3</span>
            <span><i class="inline-block h-2 w-2 rounded-xs bg-ok align-middle" /> -18 to -3</span>
            <span><i class="inline-block h-2 w-2 rounded-xs bg-warn align-middle" /> -60 to -18 dBFS</span>
            <span>blank: silence</span>
            <span><i class="inline-block h-2 w-2 rounded-xs bg-fg align-middle" /> clip</span>
            <span><i class="inline-block h-2 w-2 rounded-xs bg-accent align-middle" /> dropout</span>
        </div>
        <div class="mt-0.5 flex gap-1">
            <!-- channel numbers beside their rows -->
            <div class="flex w-4 flex-col gap-px text-right font-mono text-[8px] text-fg-3">
                <span v-for="c in channels" :key="c" :style="{ height: rowH + 'px', lineHeight: rowH + 'px' }">{{
                    c - 1
                }}</span>
            </div>
            <div
                ref="box"
                class="relative min-w-0 flex-1 cursor-crosshair"
                @mouseenter="enter"
                @mousemove="move"
                @mouseleave="leave"
            >
                <canvas ref="canvas" class="block w-full rounded" />
                <svg
                    class="mt-1 block w-full rounded bg-bg-2"
                    viewBox="0 0 1000 48"
                    preserveAspectRatio="none"
                    :style="{ height: lufsH + 'px' }"
                >
                    <line
                        x1="0"
                        x2="1000"
                        :y1="targetY"
                        :y2="targetY"
                        stroke="var(--fg-3)"
                        stroke-dasharray="4 3"
                        vector-effect="non-scaling-stroke"
                    />
                    <path
                        v-for="l in lufsPaths"
                        :key="l.p"
                        :d="l.d"
                        fill="none"
                        :stroke="l.colour"
                        stroke-width="1.5"
                        vector-effect="non-scaling-stroke"
                    />
                </svg>
                <div
                    v-if="hover"
                    class="pointer-events-none absolute inset-y-0 w-px bg-fg-2"
                    :style="{ left: hover.at + '%' }"
                />
            </div>
        </div>
        <div class="flex gap-1">
            <div class="w-4" />
            <div class="relative h-3.25 flex-1 text-[10px] text-fg-3">
                <span
                    v-for="(t, i) in ticks"
                    :key="i"
                    class="absolute -translate-x-1/2 whitespace-nowrap first:translate-x-0 last:-translate-x-full"
                    :style="{ left: t.x + '%' }"
                    >{{ t.label }}</span
                >
            </div>
        </div>
        <div class="mt-0.5 flex flex-wrap gap-x-2.5 pl-5 text-[10px] text-fg-3">
            <span>short-term LUFS · dashed -23</span>
            <span v-for="l in lufsPaths" :key="l.p"
                ><i class="inline-block h-0.5 w-3 align-middle" :style="{ background: l.colour }" />
                {{ pairLabel(l.p) }}</span
            >
        </div>
        <Teleport to="body">
            <div
                v-if="hover && tip"
                class="pointer-events-none fixed z-30 rounded-md border border-line-strong bg-bg-3 px-2 py-1.5 text-xs"
                :style="{ left: hover.x + 'px', top: hover.y + 'px' }"
            >
                <div class="mb-1 text-fg-2">{{ tip.when }}</div>
                <table class="font-mono text-[11px] tabular-nums">
                    <tr class="text-[10px] text-fg-3">
                        <td class="pr-2">ch</td>
                        <td class="pr-2 text-right">peak</td>
                        <td class="pr-2 text-right">quietest</td>
                        <td />
                    </tr>
                    <tr v-for="r in tip.rows" :key="r.c">
                        <td class="pr-2 text-fg-3">{{ r.c - 1 }}</td>
                        <td class="pr-2 text-right" :class="r.peak > -3 ? 'text-bad' : ''">{{ fmtDB(r.peak) }}</td>
                        <td class="pr-2 text-right" :class="r.dropout ? 'text-accent' : 'text-fg-2'">
                            {{ fmtDB(r.floor) }}
                        </td>
                        <td class="space-x-2">
                            <span v-if="r.clips" class="text-bad">{{ r.clips }} clipped</span
                            ><span v-if="r.dropout" class="text-accent">dropout</span>
                        </td>
                    </tr>
                </table>
                <div v-if="tip.lufs.length" class="mt-1 font-mono text-[11px] tabular-nums">
                    <span v-for="l in tip.lufs" :key="l.p" class="mr-2"
                        ><span class="text-fg-3">{{ pairLabel(l.p) }}</span> {{ fmtDB(l.l) }}</span
                    >
                    <span class="text-fg-3">LUFS</span>
                </div>
            </div>
        </Teleport>
    </div>
</template>
